import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { api } from '../../lib/api';

const PortalResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!password || !confirm) { setError('Please fill in both fields.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (!token) { setError('Invalid reset link.'); return; }
    setLoading(true);
    setError('');
    try {
      await api.clientAuth.resetPassword(token, password);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#F8FAFC',
      backgroundImage: 'radial-gradient(ellipse at 20% 0%, rgba(13,148,136,0.08) 0%, transparent 50%)',
      padding: '20px',
    }}>
      <div style={{
        width: '100%', maxWidth: '420px', background: 'white',
        borderRadius: '16px', padding: '40px 36px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid #E2E8F0',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <img src="/logo.png" alt="VOP CRM" width={140} style={{ marginBottom: '16px' }} />
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1E293B' }}>Reset Password</h1>
        </div>

        {success ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 16px',
              background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon icon="solar:check-circle-bold" width={36} style={{ color: '#10B981' }} />
            </div>
            <h2 style={{ color: '#1E293B', fontSize: '18px', margin: '0 0 8px' }}>Password Reset!</h2>
            <p style={{ color: '#64748B', fontSize: '14px', margin: '0 0 24px' }}>Your password has been updated. You can now log in.</p>
            <button onClick={() => navigate('/portal/login')} style={{
              width: '100%', padding: '12px', border: 'none', borderRadius: '10px', cursor: 'pointer',
              background: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)',
              color: 'white', fontWeight: 600, fontSize: '15px', fontFamily: 'inherit',
            }}>
              Go to Login
            </button>
          </div>
        ) : (
          <>
            {error && (
              <div style={{
                background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px',
                padding: '10px 14px', marginBottom: '16px', color: '#DC2626', fontSize: '13px',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <Icon icon="solar:danger-circle-bold" width={16} />
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} onKeyDown={e => e.key === 'Enter' && handleSubmit()}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>New Password</label>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px', border: '1px solid #E2E8F0',
                  borderRadius: '10px', padding: '10px 14px', background: '#F8FAFC',
                }}>
                  <Icon icon="solar:lock-bold-duotone" width={18} style={{ color: '#94A3B8' }} />
                  <input type="password" placeholder="At least 8 characters" value={password} onChange={e => setPassword(e.target.value)}
                    style={{ flex: 1, border: 'none', outline: 'none', background: 'none', fontSize: '14px', color: '#1E293B', fontFamily: 'inherit' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Confirm Password</label>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px', border: '1px solid #E2E8F0',
                  borderRadius: '10px', padding: '10px 14px', background: '#F8FAFC',
                }}>
                  <Icon icon="solar:lock-check-bold-duotone" width={18} style={{ color: '#94A3B8' }} />
                  <input type="password" placeholder="Confirm your password" value={confirm} onChange={e => setConfirm(e.target.value)}
                    style={{ flex: 1, border: 'none', outline: 'none', background: 'none', fontSize: '14px', color: '#1E293B', fontFamily: 'inherit' }} />
                </div>
              </div>
              <button onClick={handleSubmit} disabled={loading} style={{
                width: '100%', padding: '12px', border: 'none', borderRadius: '10px', cursor: 'pointer',
                background: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)',
                color: 'white', fontWeight: 600, fontSize: '15px', fontFamily: 'inherit',
                opacity: loading ? 0.7 : 1,
              }}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
              <div style={{ textAlign: 'center' }}>
                <Link to="/portal/login" style={{ color: '#0D9488', fontSize: '13px', textDecoration: 'none', fontWeight: 500 }}>
                  Back to Login
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PortalResetPassword;
